import React from 'react';
import { Button, ButtonGroup, Form } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { GlowpickPaginationInfo as PaginationInfo } from '../../../../models/Glowpick';

type NavigationTab = 'newest' | 'awards' | 'brands' | 'category' | 'search';

interface GlowpickPaginationProps {
  paginationInfo: PaginationInfo;
  activeTab: NavigationTab;
  selectedCategory?: string;
  selectedSubcategory?: string;
  selectedBrand?: string;
  selectedAwardPeriod?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const GlowpickPagination: React.FC<GlowpickPaginationProps> = ({
  paginationInfo,
  activeTab,
  selectedCategory,
  selectedSubcategory,
  selectedBrand,
  selectedAwardPeriod,
  onPageChange,
  onPageSizeChange,
}) => {
  const { currentPage, totalPages, totalProducts, productsPerPage } = paginationInfo;

  // Get context info based on active tab
  const getContextInfo = () => {
    switch (activeTab) {
      case 'awards':
        return selectedAwardPeriod ? `in ${selectedAwardPeriod} awards` : 'in awards';
      case 'brands':
        return selectedBrand ? `for ${selectedBrand}` : 'across all brands';
      case 'category':
        if (selectedSubcategory) {
          return `in ${selectedCategory} › ${selectedSubcategory}`;
        } else if (selectedCategory) {
          return `in ${selectedCategory}`;
        }
        return 'across all categories';
      case 'search':
        return 'in search results';
      case 'newest':
      default:
        return 'newest products';
    }
  };

  const contextInfo = getContextInfo();

  // Generate page numbers to show
  const getVisiblePageNumbers = (): number[] => {
    const delta = 2; // Show 2 pages on each side of current page
    const pages: number[] = [];
    const start = Math.max(1, currentPage - delta);
    const end = Math.min(totalPages, currentPage + delta);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = getVisiblePageNumbers();
  console.info('🎯 Visible pages:', visiblePages, 'Current page:', currentPage);

  if (totalProducts === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-3 border-top bg-light">
        <span className="text-muted">No products found {contextInfo}</span>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2 p-3 border-top bg-light">
      {/* Summary Info */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">
            {totalProducts.toLocaleString()} products {contextInfo}
          </span>
        </div>

        {/* Page Size Selector */}
        <div className="d-flex align-items-center gap-2">
          <label className="text-muted small mb-0">Per page:</label>
          <Form.Select
            size="sm"
            value={productsPerPage}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={300}>300</option>
          </Form.Select>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2">
          {/* First Page */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            title="First page"
          >
            <ChevronsLeft size={16} />
          </Button>

          {/* Previous Page */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
          >
            <ChevronLeft size={16} />
          </Button>

          {/* Page Numbers */}
          <ButtonGroup>
            {/* Show first page if not in visible range */}
            {visiblePages.length > 0 && visiblePages[0]! > 1 && (
              <>
                <Button variant="outline-secondary" size="sm" onClick={() => onPageChange(1)}>
                  1
                </Button>
                {visiblePages[0]! > 2 && (
                  <Button variant="outline-secondary" size="sm" disabled>
                    ...
                  </Button>
                )}
              </>
            )}

            {/* Visible page numbers */}
            {visiblePages.map((page) => {
              const isActive = page === currentPage;
              console.info(`🎯 Page ${page}: isActive=${isActive}, currentPage=${currentPage}`);
              return (
                <Button
                  key={page}
                  variant={isActive ? 'danger' : 'outline-secondary'}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className={isActive ? 'fw-bold' : ''}
                  style={isActive ? { backgroundColor: '#eb3d48', borderColor: '#eb3d48' } : undefined}
                >
                  {page}
                </Button>
              );
            })}

            {/* Show last page if not in visible range */}
            {visiblePages.length > 0 && visiblePages[visiblePages.length - 1]! < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1]! < totalPages - 1 && (
                  <Button variant="outline-secondary" size="sm" disabled>
                    ...
                  </Button>
                )}
                <Button variant="outline-secondary" size="sm" onClick={() => onPageChange(totalPages)}>
                  {totalPages}
                </Button>
              </>
            )}
          </ButtonGroup>

          {/* Next Page */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            <ChevronRight size={16} />
          </Button>

          {/* Last Page */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            title="Last page"
          >
            <ChevronsRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};
